function getTargetNumber(value) {
  if (typeof value === 'string') {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  return value.productNumber || value.categoryNumber || '';
}

function getProductCategoryNumbers(product) {
  return Array.isArray(product?.categories)
    ? product.categories
        .map((category) => getTargetNumber(category))
        .filter(Boolean)
    : [];
}

function buildEmptyDiscount(product) {
  const originalPrice = Number(product?.price) || 0;

  return {
    hasDiscount: false,
    discountType: null,
    discountValue: 0,
    discountAmount: 0,
    discountedPrice: originalPrice,
    originalPrice,
    couponCode: null,
    couponLabel: null,
    couponName: null,
  };
}

export function getProductDiscount(product, coupons = []) {
  if (!product) {
    return buildEmptyDiscount(product);
  }

  const originalPrice = Number(product.price) || 0;

  // Product-level sale price takes precedence over coupon discounts.
  // `product.salePrice` is the locale-resolved sale price set directly on the product.
  const nativeSalePrice = Number(product.salePrice) || 0;
  if (nativeSalePrice > 0 && nativeSalePrice < originalPrice) {
    const discountAmount = originalPrice - nativeSalePrice;
    const discountValue =
      originalPrice > 0
        ? Math.round((discountAmount / originalPrice) * 100)
        : 0;
    return {
      hasDiscount: true,
      discountType: 'percentage',
      discountValue,
      discountAmount,
      discountedPrice: nativeSalePrice,
      originalPrice,
      couponCode: null,
      couponLabel: null,
      couponName: null,
    };
  }

  if (!Array.isArray(coupons) || coupons.length === 0) {
    return buildEmptyDiscount(product);
  }

  const productNumber = product.productNumber;
  const productCategories = getProductCategoryNumbers(product);

  if (!productNumber || !Number.isFinite(originalPrice)) {
    return buildEmptyDiscount(product);
  }

  for (const coupon of coupons) {
    if (!coupon?.isActiveNow || coupon?.isDeleted || coupon?.isExpired) {
      continue;
    }

    const excludeProducts = Array.isArray(coupon.excludeProducts)
      ? coupon.excludeProducts.map((entry) => getTargetNumber(entry))
      : [];
    const excludeCategories = Array.isArray(coupon.excludeCategories)
      ? coupon.excludeCategories.map((entry) => getTargetNumber(entry))
      : [];

    const isExcludedByProduct = excludeProducts.includes(productNumber);
    const isExcludedByCategory = productCategories.some((categoryNumber) =>
      excludeCategories.includes(categoryNumber),
    );

    if (isExcludedByProduct || isExcludedByCategory) {
      continue;
    }

    for (const benefit of coupon.benefits || []) {
      if (!['percentage', 'fixed'].includes(benefit?.type)) {
        continue;
      }

      const applicableProducts = Array.isArray(benefit.applicableProducts)
        ? benefit.applicableProducts.map((entry) => getTargetNumber(entry))
        : [];
      const applicableCategories = Array.isArray(benefit.applicableCategories)
        ? benefit.applicableCategories.map((entry) => getTargetNumber(entry))
        : [];

      const matchesProduct =
        applicableProducts.length === 0 ||
        applicableProducts.includes(productNumber);
      const matchesCategory =
        applicableCategories.length === 0 ||
        productCategories.some((categoryNumber) =>
          applicableCategories.includes(categoryNumber),
        );

      if (!matchesProduct || !matchesCategory) {
        continue;
      }

      const discountValue = Number(benefit.value) || 0;
      let discountAmount = 0;

      if (benefit.type === 'percentage') {
        discountAmount = (originalPrice * discountValue) / 100;

        if (benefit.maxAmount) {
          discountAmount = Math.min(
            discountAmount,
            Number(benefit.maxAmount) || discountAmount,
          );
        }
      } else {
        discountAmount = discountValue;
      }

      const discountedPrice = Math.max(0, originalPrice - discountAmount);

      return {
        hasDiscount: true,
        discountType: benefit.type,
        discountValue,
        discountAmount,
        discountedPrice,
        originalPrice,
        couponCode: coupon.code || null,
        couponLabel: coupon.label || '🎁',
        couponName: coupon.name || null,
      };
    }
  }

  return buildEmptyDiscount(product);
}
